class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindUpsellButton();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindUpsellButton() {
    this.addEventListener("click", (event) => {
      const upsellButton = event.target.closest(".upsell-add-button");
      if (upsellButton) {
        event.preventDefault();

        const form = upsellButton.closest(".upsell-form");
        const variantId = form.querySelector('input[name="id"]').value;

        this.addItemToCart(variantId);
      }
    });
  }

  async addItemToCart(variantId) {
    await window.fourPawCartWork?.settled;
    const finish = window.fourPawCartWork?.begin();
    if (window.fourPawCartWork && !finish) return;
    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    });

    try {
      const request = window.fourPawCartFetch || window.fetch.bind(window);
      const response = await request(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        body,
      });

      if (response.status === 422) {
        const errorData = await response.json();
        throw new Error(errorData.description || "Unable to add this item.");
      }
      if (!response.ok) throw new Error("Unable to add this item.");

      const parsedState = await response.json();
      this.renderContents(parsedState);

      const controller = this.querySelector("cart-drawer-items") || document.querySelector("cart-items");
      if (controller?.normalizeFourPawSafeWalkCart) {
        const normalized = await controller.normalizeFourPawSafeWalkCart();
        await controller.fourPawRefreshCart(normalized.cart, true);
      }
    } catch (error) {
      window.fourPawCartWork?.fail();
      const errors = document.getElementById("CartDrawer-CartErrors");
      if (errors) errors.textContent = error instanceof Error ? error.message : window.cartStrings.error;
      console.error("Error adding item to cart:", error);
    } finally {
      finish?.();
    }
  }

  cartStateMatches(cartData) {
    if (!Array.isArray(cartData?.items)) return false;
    if (cartData.items.length === 0) return this.classList.contains("is-empty");

    const rows = Array.from(this.querySelectorAll("#CartDrawer-CartItems .cart-item[data-line-key]"));
    if (rows.length !== cartData.items.length) return false;

    const renderedLines = rows.map((row) => [
      String(row.dataset.lineKey || ""),
      Number(row.dataset.lineQuantity || 0),
      Number(row.dataset.lineFinalPrice || 0),
    ]).sort((left, right) => left[0].localeCompare(right[0]));
    const cartLines = cartData.items.map((item) => [
      String(item.key || ""),
      Number(item.quantity || 0),
      Number(item.final_line_price || 0),
    ]).sort((left, right) => left[0].localeCompare(right[0]));

    if (renderedLines.some((line, index) => line.some((value, part) => value !== cartLines[index][part]))) {
      return false;
    }

    const renderedTotal = Number(this.querySelector(".gb-totals-total-value")?.textContent?.trim());
    return !Number.isFinite(Number(cartData.total_price)) ||
      !Number.isFinite(renderedTotal) ||
      renderedTotal === Number(cartData.total_price);
  }

  /**
   * Refresh cart drawer after AJAX cart operations
   * Can be called directly or will be triggered by 'cart:ajax-update' event
   * @param {Object} sectionsData - Optional sections data from cart response
   */
  async refreshCartDrawer(sectionsData = null, openDrawer = true, preserveOpenState = false, strict = false, force = false) {
    const render = (state) => {
      if (strict && this.getSectionsToRender().some((section) =>
        !state.sections?.[section.id] || !this.getSectionDOM(state.sections[section.id], section.selector)
      )) throw new Error("The updated cart sections are unavailable.");
      this.renderContents(state, openDrawer, preserveOpenState);
    };
    const knownCart = Array.isArray(sectionsData?.items) &&
      Number.isFinite(Number(sectionsData?.item_count)) &&
      Number.isFinite(Number(sectionsData?.total_price))
      ? sectionsData
      : null;
    if (knownCart && !force && this.cartStateMatches(knownCart)) {
      this.classList.toggle("is-empty", knownCart.item_count === 0);
      if (typeof updateBubbleCount === "function") updateBubbleCount(knownCart.item_count || 0);
      return false;
    }

    // Native cart-page mutations include their own section set. A valid cart
    // with only page sections still needs one fetch for the drawer and icon.
    const completeDrawerSections = sectionsData?.sections && this.getSectionsToRender().every(
      (section) => Boolean(sectionsData.sections[section.id]),
    );
    if (sectionsData?.sections && (!knownCart || completeDrawerSections)) {
      const itemCount = Number.isFinite(Number(sectionsData.item_count))
        ? Number(sectionsData.item_count)
        : this.getItemCountFromDrawerHTML(sectionsData.sections["cart-drawer"]);
      const dataToRender = {
        ...sectionsData,
        id: sectionsData.id || this.productId || null,
        item_count: Number.isFinite(itemCount) ? itemCount : 0,
      };
      render(dataToRender);
      return true;
    }

    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set("sections", sections.join(","));
      if (window.Shopify?.theme?.role === "unpublished") {
        url.searchParams.set("preview_theme_id", String(window.Shopify.theme.id));
      }
      const request = window.fourPawCartFetch || window.fetch.bind(window);
      const response = await request(url, { cache: "no-store" });
      
      if (!response.ok) {
        throw new Error("Unable to refresh the cart.");
      }

      const parsedState = await response.json();
      let itemCount = knownCart?.item_count ?? parsedState.item_count;

      // Wrap return in sections object for renderContents
      let sectionsData = null;
      if (parsedState.sections) {
        sectionsData = parsedState.sections;
      } else {
        sectionsData = {};
        const sectionIds = this.getSectionsToRender().map((section) => section.id);
        sectionIds.forEach((sectionId) => {
          if (parsedState[sectionId]) {
            sectionsData[sectionId] = parsedState[sectionId];
          }
        });
      }

      if (!sectionsData || Object.keys(sectionsData).length === 0) {
        throw new Error("The updated cart sections are unavailable.");
      }

      if (!Number.isFinite(itemCount) && sectionsData["cart-drawer"]) {
        const drawerDocument = new DOMParser().parseFromString(sectionsData["cart-drawer"], "text/html");
        itemCount = Array.from(drawerDocument.querySelectorAll('input[name="updates[]"]')).reduce(
          (total, input) => total + (Number(input.value) || 0),
          0,
        );
      }

      const dataToRender = {
        ...(knownCart || {}),
        sections: sectionsData,
        id: knownCart?.id || parsedState.id || this.productId || null,
        item_count: Number.isFinite(itemCount) ? itemCount : 0
      };
      
      render(dataToRender);
      return true;
    } catch (error) {
      if (strict) throw error;
      console.error("Error refreshing cart drawer:", error);
      return false;
    }
  }

  async synchronizeCart(forceRefresh = false) {
    this.fourPawForceRefresh = Boolean(this.fourPawForceRefresh || forceRefresh);
    if (this.fourPawSync) return this.fourPawSync;
    this.fourPawSync = (async () => {
      let finalCart = null;
      do {
        const force = Boolean(this.fourPawForceRefresh || window.fourPawCartWork?.needsSync);
        this.fourPawForceRefresh = false;
        await window.fourPawCartWork?.settled;
        const finish = window.fourPawCartWork?.begin({ recovery: true });
        if (window.fourPawCartWork && !finish) {
          this.fourPawForceRefresh = Boolean(this.fourPawForceRefresh || force);
          continue;
        }
        try {
          const controller = document.querySelector("cart-drawer-items, cart-items");
          if (controller?.normalizeFourPawSafeWalkCart) {
            const result = await controller.normalizeFourPawSafeWalkCart();
            finalCart = await controller.fourPawRefreshCart(result.cart, true, force);
          } else {
            await this.refreshCartDrawer(null, false, true, true, force);
            if (window.fourPawCartWork) window.fourPawCartWork.needsSync = false;
          }
        } catch (error) {
          window.fourPawCartWork?.fail();
          const errors = document.getElementById("CartDrawer-CartErrors");
          if (errors) errors.textContent = error?.fourPawOfferPrice ? error.message : window.cartStrings.error;
          console.error(error);
        } finally {
          finish?.();
        }
      } while (this.fourPawForceRefresh);
      return finalCart;
    })();
    try { return await this.fourPawSync; }
    finally { this.fourPawSync = null; }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      if (window.fourPawCartWork?.needsSync) this.synchronizeCart(true);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");


    this.dispatchEvent(new CustomEvent("cart-drawer:open", {
      bubbles: true,
      detail: { triggeredBy }
    }));
  }

  close() {
    // Check if cart customization mode is active
    const cartCustomizationMode = document.querySelector("cart-drawer").classList.contains("customization-mode");
    if (cartCustomizationMode) {
      alert("Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing.");
      return;
    }

    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");

  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, openDrawer = true, preserveOpenState = false) {
    if (!parsedState?.sections) {
      this.refreshCartDrawer();
      return;
    }

    if (!Number.isFinite(Number(parsedState.item_count))) {
      const itemCount = this.getItemCountFromDrawerHTML(parsedState.sections["cart-drawer"]);
      parsedState = {
        ...parsedState,
        item_count: Number.isFinite(itemCount) ? itemCount : 0,
      };
    }

    const drawerInner = this.querySelector(".drawer__inner");
    drawerInner?.classList.contains("is-empty") && drawerInner.classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];

      if (!sectionElement || !sectionHtml) return;
      if (section.id == "cart-drawer" || section.selector == "#CartDrawer") {
        const newCartDrawer = this.getSectionDOM(sectionHtml, section.selector);
        if (!newCartDrawer) return;
        updateCartDrawer(sectionElement, newCartDrawer);
      } else {
        if (section.id === "cart-icon-bubble") {
          const iconType = sectionElement.getAttribute("data-cart-icon-type");
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (iconType) {
            sectionElement.setAttribute("data-cart-icon-type", iconType);
          }
          const iconWrapper = sectionElement.querySelector(".svg-wrapper");
          if (iconWrapper && iconType) {
            const bagIcon = iconWrapper.querySelector(".cart-icon-bag");
            const cartIcon = iconWrapper.querySelector(".cart-icon-cart");
            const cartIconEmpty = iconWrapper.querySelector(".cart-icon-cart-empty");
            
            if (iconType === "bag") {
              if (bagIcon) bagIcon.style.display = "flex";
            } 
            else {
              const cartEmpty = parsedState.item_count === 0;
              if (cartEmpty && cartIconEmpty) {
                cartIconEmpty.style.display = "flex";
              } else if (cartIcon) {
                cartIcon.style.display = "flex";
              }
            }
          }
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        }
      }
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
      
      this.dispatchEvent(new CustomEvent("cart-drawer:render", {
        bubbles: true,
        detail: { parsedState }
      }));

        
      if (preserveOpenState) return;
      if (openDrawer) {
        this.open();
      } else {
        this.classList.remove("active");
        document.body.classList.remove("overflow-hidden");
      }
    });

  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  getItemCountFromDrawerHTML(html) {
    if (!html) return Number.NaN;
    const drawerDocument = new DOMParser().parseFromString(html, "text/html");
    return Array.from(drawerDocument.querySelectorAll('input[name="updates[]"]')).reduce(
      (total, input) => total + (Number(input.value) || 0),
      0,
    );
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

function setupAjaxCartHandler() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;

  document.addEventListener("cart:ajax-update", async function (event) {
    const sectionsData = event.detail?.sectionsData || null;
    await cartDrawer.refreshCartDrawer(sectionsData);
  });

  window.refreshCartDrawer = async function (sectionsData = null) {
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      await drawer.refreshCartDrawer(sectionsData);
    }
  };
}

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", setupAjaxCartHandler);
} else {
  setupAjaxCartHandler();
}

window.addEventListener("pageshow", async (event) => {
  const navigationEntry = window.performance?.getEntriesByType?.("navigation")?.[0];
  if (!event.persisted && navigationEntry?.type !== "back_forward") return;

  const drawer = document.querySelector("cart-drawer");
  if (!drawer || typeof drawer.refreshCartDrawer !== "function") return;
  await drawer.synchronizeCart();
});

function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) {
    return; // Already defined
  }

  const CartItemsElement = customElements.get("cart-items");
  if (!CartItemsElement) return;

  class CartDrawerItems extends CartItemsElement {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}

if (customElements.get("cart-items")) {
  defineCartDrawerItems();
} else {
  customElements.whenDefined("cart-items").then(defineCartDrawerItems);
}
